import React, { useState, useEffect, useContext, createContext } from 'react';
import StationsView from "./StationsView";
import CompactStationMap from "./CompastStationMap";

// Auth Context
const AuthContext = createContext();

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const hostname = window && window.location && window.location.hostname;

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const backendUrl = hostname === "evchargedata.com" ? "https://www.evchargedata.com" : "http://localhost:8080";
  const url = `${backendUrl}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  return fetch(url, config);
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      // Verify token validity
      apiCall('/api/stations', {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {
        localStorage.removeItem('token');
        setToken(null);
      });
    }
  }, [token]);

  const login = async (email, password) => {
    const response = await apiCall('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (response.ok) {
      const data = await response.json();
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      return { success: true };
    } else {
      const error = await response.text();
      return { success: false, error };
    }
  };

  const register = async (userData) => {
    const response = await apiCall('/api/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    if (response.ok) {
      const data = await response.json();
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      return { success: true };
    } else {
      const error = await response.text();
      return { success: false, error };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Main App Component
const App = () => {
  const [currentView, setCurrentView] = useState('map');
  const [selectedStation, setSelectedStation] = useState(null);

  return (
    <AuthProvider>
      <div className="min-h-screen bg-gray-50">
        <Header
          currentView={currentView}
          setCurrentView={setCurrentView}
        />

        <main className="pt-16">
          {currentView === 'map' && (
            <StationsView
              selectedStation={selectedStation}
              setSelectedStation={setSelectedStation}
              setCurrentView={setCurrentView}
            />
          )}
          {currentView === 'station' && selectedStation && (
            <StationDetails
              station={selectedStation}
              setCurrentView={setCurrentView}
            />
          )}
          {currentView === 'admin' && <AdminDashboard />}
          {currentView === 'auth' && <AuthModal setCurrentView={setCurrentView} />}
        </main>
      </div>
    </AuthProvider>
  );
};

// Header Component
const Header = ({ currentView, setCurrentView }) => {
  const { user, logout } = useAuth();

  return (
    <header className="fixed top-0 left-0 right-0 bg-white border-b z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <h1 className="text-xl font-bold text-blue-600">EVStations</h1>

          <nav className="flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-4">
                {user.is_admin && (
                  <button
                    onClick={() => setCurrentView('admin')}
                    className={`px-4 py-2 rounded ${
                      currentView === 'admin'
                        ? 'bg-purple-100 text-purple-700'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Admin
                  </button>
                )}
                <span className="text-gray-700">{user.username}</span>
                <button
                  onClick={logout}
                  className="px-4 py-2 text-red-600 hover:text-red-800"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={() => setCurrentView('auth')}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Sign In
              </button>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

// Station Card Component (unchanged)
const StationCard = ({ station, onClick }) => {
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded border p-6 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-semibold text-lg text-gray-900">
          {station.name || 'Unnamed Station'}
        </h3>
        {station.max_power && (
          <span className="bg-green-100 text-green-800 text-sm px-2 py-1 rounded">
            {station.max_power}kW
          </span>
        )}
      </div>

      <div className="flex items-center mb-3">
        {station.average_rating > 0 ? (
          <div className="flex items-center">
            <div className="flex">{renderStars(station.average_rating)}</div>
            <span className="text-sm text-gray-600 ml-2">
              ({station.average_rating.toFixed(1)})
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-500">No ratings yet</span>
        )}
      </div>

      <div className="text-sm text-gray-600 mb-4">
        <div className="flex items-center mb-2">
          <span>📍 {station.full_address || `${station.city}, ${station.country}`}</span>
        </div>

        {station.network && (
          <div className="text-blue-600">
            🔌 {station.network}
          </div>
        )}

        {station.distance_km && (
          <div className="text-gray-500 mt-1">
            📏 {station.distance_km.toFixed(1)} km away
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{station.comments?.length || 0} comments • {station.pictures?.length || 0} photos</span>
        <span className="text-blue-600 font-medium">View Details →</span>
      </div>
    </div>
  );
};

// New Station List Component
const StationList = ({ stations, onStationClick, sortBy, sortOrder, onSortChange }) => {
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}>
          ★
        </span>
      );
    }
    return stars;
  };

  const SortableHeader = ({ field, children }) => (
    <th
      className="px-4 py-3 text-left cursor-pointer hover:bg-gray-50 select-none"
      onClick={() => onSortChange(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortBy === field && (
          <span className="text-blue-600">
            {sortOrder === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className="bg-white rounded border overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <SortableHeader field="name">Station Name</SortableHeader>
            <th className="px-4 py-3 text-left">Location</th>
            <SortableHeader field="distance">Distance</SortableHeader>
            <SortableHeader field="power">Power</SortableHeader>
            <th className="px-4 py-3 text-left">Rating</th>
            <th className="px-4 py-3 text-left">Network</th>
            <SortableHeader field="updated">Last Updated</SortableHeader>
            <th className="px-4 py-3 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {stations.map((station, index) => (
            <tr
              key={station.id}
              className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}
            >
              <td className="px-4 py-4">
                <div className="font-medium text-gray-900">
                  {station.name || 'Unnamed Station'}
                </div>
                {station.operator_name && (
                  <div className="text-sm text-gray-500">
                    {station.operator_name}
                  </div>
                )}
              </td>

              <td className="px-4 py-4">
                <div className="text-sm text-gray-900">
                  {station.city}, {station.country}
                </div>
                {station.full_address && (
                  <div className="text-xs text-gray-500 truncate max-w-xs">
                    {station.full_address}
                  </div>
                )}
              </td>

              <td className="px-4 py-4 text-sm text-gray-900">
                {station.distance_km ? `${station.distance_km.toFixed(1)} km` : '-'}
              </td>

              <td className="px-4 py-4">
                {station.max_power ? (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    {station.max_power}kW
                  </span>
                ) : (
                  <span className="text-gray-500 text-sm">-</span>
                )}
              </td>

              <td className="px-4 py-4">
                {station.average_rating > 0 ? (
                  <div className="flex items-center">
                    <div className="flex text-sm">{renderStars(station.average_rating)}</div>
                    <span className="text-xs text-gray-600 ml-1">
                      ({station.average_rating.toFixed(1)})
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-500 text-sm">No ratings</span>
                )}
              </td>

              <td className="px-4 py-4 text-sm">
                {station.network ? (
                  <span className="text-blue-600">{station.network}</span>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </td>

              <td className="px-4 py-4 text-sm text-gray-500">
                {station.updated_at ?
                  new Date(station.updated_at).toLocaleDateString() :
                  new Date(station.created_at).toLocaleDateString()
                }
              </td>

              <td className="px-4 py-4">
                <button
                  onClick={() => onStationClick(station)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Station Details Component
const StationDetails = ({ station, setCurrentView }) => {
  const { user, token } = useAuth();
  const [stationData, setStationData] = useState(station);
  const [activeTab, setActiveTab] = useState('info');
  const [newComment, setNewComment] = useState('');
  const [newRating, setNewRating] = useState(5);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadStationDetails();
  }, [station.id]);

  const loadStationDetails = async () => {
    try {
      const response = await apiCall(`/api/stations/${station.id}`);
      if (response.ok) {
        const data = await response.json();
        setStationData(data);
      }
    } catch (error) {
      console.error('Error loading station details:', error);
    }
  };

  const handleAddComment = async () => {
    if (!user || !newComment.trim()) return;

    try {
      const response = await apiCall(`/api/stations/${station.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newComment,
          rating: newRating
        })
      });

      if (response.ok) {
        setNewComment('');
        setNewRating(5);
        loadStationDetails();
        alert('Comment submitted for approval!');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await apiCall(`/api/stations/${station.id}/pictures`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        loadStationDetails();
        alert('Image uploaded for approval!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploading(false);
    }
  };

  const renderStars = (rating, interactive = false, onRatingChange = null) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          className={`cursor-pointer text-2xl ${
            i <= rating ? 'text-yellow-400' : 'text-gray-300'
          }`}
          onClick={interactive ? () => onRatingChange(i) : undefined}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => setCurrentView('map')}
          className="text-blue-600 hover:text-blue-800 mb-4"
        >
          ← Back to Map
        </button>

        <div className="bg-white rounded border p-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {stationData.name || 'Unnamed Station'}
              </h1>
              <div className="text-gray-600">
                📍 {stationData.full_address || `${stationData.city}, ${stationData.country}`}
                {stationData.max_power && (
                  <span className="ml-4 bg-green-100 text-green-800 px-3 py-1 rounded text-sm">
                    {stationData.max_power}kW
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4 lg:mt-0">
              {stationData.average_rating > 0 ? (
                <div className="flex items-center">
                  <div className="flex">{renderStars(stationData.average_rating)}</div>
                  <span className="text-lg font-semibold text-gray-700 ml-2">
                    {stationData.average_rating.toFixed(1)}
                  </span>
                </div>
              ) : (
                <span className="text-gray-500">No ratings yet</span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <nav className="flex space-x-8">
              {[
                { id: 'info', label: 'Information' },
                { id: 'comments', label: 'Comments' },
                { id: 'photos', label: 'Photos' },
                { id: 'map', label: 'Map' }
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded border p-8">
        {activeTab === 'info' && (
          <div>
            <h2 className="text-xl font-semibold mb-6">Station Information</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-2">Location Details</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  {stationData.street && <p><strong>Address:</strong> {stationData.street}</p>}
                  {stationData.city && <p><strong>City:</strong> {stationData.city}</p>}
                  {stationData.postcode && <p><strong>Postal Code:</strong> {stationData.postcode}</p>}
                  {stationData.country && <p><strong>Country:</strong> {stationData.country}</p>}
                </div>
              </div>

              <div>
                {stationData.network && (
                  <div className="mb-4">
                    <h3 className="font-medium mb-2">Network</h3>
                    <p className="text-sm text-gray-600">{stationData.network}</p>
                  </div>
                )}

                {stationData.operator_name && (
                  <div>
                    <h3 className="font-medium mb-2">Operator</h3>
                    <p className="text-sm text-gray-600">{stationData.operator_name}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'comments' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Comments & Reviews</h2>
              <span className="text-sm text-gray-500">
                {stationData.comments?.length || 0} comments
              </span>
            </div>

            {user && (
              <div className="bg-gray-50 rounded p-6 mb-6">
                <h3 className="font-medium mb-4">Add a Review</h3>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Rating</label>
                  <div className="flex">
                    {renderStars(newRating, true, setNewRating)}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Comment</label>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your experience..."
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                  />
                </div>

                <button
                  onClick={handleAddComment}
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                >
                  Submit Review
                </button>
              </div>
            )}

            <div className="space-y-4">
              {stationData.comments?.map((comment) => (
                <div key={comment.id} className="border rounded p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium">{comment.user?.username}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {comment.rating && (
                      <div className="flex">
                        {renderStars(comment.rating)}
                      </div>
                    )}
                  </div>
                  <p className="text-gray-700">{comment.content}</p>
                </div>
              ))}

              {(!stationData.comments || stationData.comments.length === 0) && (
                <p className="text-center text-gray-500 py-8">No comments yet. Be the first to review!</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Photos</h2>
              <span className="text-sm text-gray-500">
                {stationData.pictures?.length || 0} photos
              </span>
            </div>

            {user && (
              <div className="bg-gray-50 rounded p-6 mb-6">
                <h3 className="font-medium mb-4">Upload Photo</h3>
                <div className="flex items-center space-x-4">
                  <label className="bg-blue-600 text-white px-4 py-2 rounded cursor-pointer hover:bg-blue-700">
                    {uploading ? 'Uploading...' : 'Choose File'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  <span className="text-sm text-gray-500">JPG, PNG up to 10MB</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stationData.pictures?.map((picture) => (
                <div key={picture.id} className="border rounded overflow-hidden">
                  <img
                    src={`/api/pictures/${picture.filename}`}
                    alt={picture.original_filename}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-3">
                    <p className="text-xs text-gray-500">
                      By {picture.user?.username} • {new Date(picture.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}

              {(!stationData.pictures || stationData.pictures.length === 0) && (
                <div className="col-span-full text-center text-gray-500 py-8">
                  No photos yet. Upload the first one!
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'map' && (
          <CompactStationMap station={stationData} height="150px" className="mb-4" />
        )}

      </div>
    </div>
  );
};

// Admin Dashboard Component
const EDIT_FIELD_LABELS = {
  name: 'Name',
  city: 'City',
  country: 'Country',
  postcode: 'Postcode',
  street: 'Street',
  full_address: 'Full Address',
  network: 'Network',
  url: 'Website URL',
  facilities: 'Facilities',
  operator_name: 'Operator Name',
  support_phone_number: 'Support Phone',
  support_email: 'Support Email',
  support_website: 'Support Website',
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const [unapprovedContent, setUnapprovedContent] = useState({ comments: [], pictures: [] });
  const [stationEdits, setStationEdits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    await Promise.all([loadUnapprovedContent(), loadStationEdits()]);
    setLoading(false);
  };

  const loadUnapprovedContent = async () => {
    try {
      const response = await apiCall('/api/admin/unapproved', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnapprovedContent(data);
      }
    } catch (error) {
      console.error('Error loading unapproved content:', error);
    }
  };

  const loadStationEdits = async () => {
    try {
      const response = await apiCall('/api/admin/station-edits', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStationEdits(data || []);
      }
    } catch (error) {
      console.error('Error loading station edits:', error);
    }
  };

  const handleApproveComment = async (commentId) => {
    try {
      const response = await apiCall(`/api/admin/comments/${commentId}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) loadUnapprovedContent();
    } catch (error) {
      console.error('Error approving comment:', error);
    }
  };

  const handleRejectComment = async (commentId) => {
    try {
      const response = await apiCall(`/api/admin/comments/${commentId}/reject`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) loadUnapprovedContent();
    } catch (error) {
      console.error('Error rejecting comment:', error);
    }
  };

  const handleApprovePicture = async (pictureId) => {
    try {
      const response = await apiCall(`/api/admin/pictures/${pictureId}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) loadUnapprovedContent();
    } catch (error) {
      console.error('Error approving picture:', error);
    }
  };

  const handleRejectPicture = async (pictureId) => {
    try {
      const response = await apiCall(`/api/admin/pictures/${pictureId}/reject`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) loadUnapprovedContent();
    } catch (error) {
      console.error('Error rejecting picture:', error);
    }
  };

  const handleApproveEdit = async (editId) => {
    try {
      const response = await apiCall(`/api/admin/station-edits/${editId}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) loadStationEdits();
    } catch (error) {
      console.error('Error approving station edit:', error);
    }
  };

  const handleRejectEdit = async (editId) => {
    try {
      const response = await apiCall(`/api/admin/station-edits/${editId}/reject`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) loadStationEdits();
    } catch (error) {
      console.error('Error rejecting station edit:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-gray-600">Review and approve user-generated content</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-500 text-white rounded p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Pending Comments</p>
              <p className="text-3xl font-bold">{unapprovedContent.comments ? unapprovedContent.comments.length : 0}</p>
            </div>
            <span className="text-4xl">💬</span>
          </div>
        </div>

        <div className="bg-purple-500 text-white rounded p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Pending Photos</p>
              <p className="text-3xl font-bold">{unapprovedContent.pictures ? unapprovedContent.pictures.length : 0}</p>
            </div>
            <span className="text-4xl">📷</span>
          </div>
        </div>

        <div className="bg-green-600 text-white rounded p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Pending Station Edits</p>
              <p className="text-3xl font-bold">{stationEdits.length}</p>
            </div>
            <span className="text-4xl">✏️</span>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="bg-white rounded border p-8 mb-8">
        <h2 className="text-xl font-semibold mb-6">Pending Comments</h2>

        {unapprovedContent.comments.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No pending comments</p>
        ) : (
          <div className="space-y-4">
            {unapprovedContent.comments.map((comment) => (
              <div key={comment.id} className="border rounded p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-medium">{comment.user?.username}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </p>
                    {comment.rating && (
                      <div className="mt-1">
                        Rating: {comment.rating}/5 ⭐
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApproveComment(comment.id)}
                      className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => handleRejectComment(comment.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                    >
                      ✗ Reject
                    </button>
                  </div>
                </div>
                <p className="text-gray-700">{comment.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pictures Section */}
      <div className="bg-white rounded border p-8 mb-8">
        <h2 className="text-xl font-semibold mb-6">Pending Photos</h2>

        {unapprovedContent.pictures.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No pending photos</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {unapprovedContent.pictures.map((picture) => (
              <div key={picture.id} className="border rounded overflow-hidden">
                <img
                  src={`/api/pictures/${picture.filename}`}
                  alt={picture.original_filename}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">{picture.user?.username}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(picture.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-xs text-gray-600 mb-4">{picture.original_filename}</p>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApprovePicture(picture.id)}
                      className="flex-1 bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700"
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => handleRejectPicture(picture.id)}
                      className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700"
                    >
                      ✗ Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Station Edits Section */}
      <div className="bg-white rounded border p-8">
        <h2 className="text-xl font-semibold mb-6">Pending Station Edits</h2>

        {stationEdits.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No pending station edits</p>
        ) : (
          <div className="space-y-4">
            {stationEdits.map((edit) => {
              const changes = Object.entries(edit.proposed_changes || {})
                .filter(([, v]) => v !== null && v !== undefined && v !== '');
              return (
                <div key={edit.id} className="border rounded p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-medium">
                        Station #{edit.evstation_id}
                        <span className="ml-2 text-sm text-gray-500">by {edit.username}</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(edit.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleApproveEdit(edit.id)}
                        className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => handleRejectEdit(edit.id)}
                        className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>

                  {changes.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No changes specified</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="pb-1 pr-4 font-medium w-40">Field</th>
                          <th className="pb-1 font-medium">Proposed value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changes.map(([key, value]) => (
                          <tr key={key} className="border-b last:border-0">
                            <td className="py-1 pr-4 text-gray-500 font-medium">
                              {EDIT_FIELD_LABELS[key] || key}
                            </td>
                            <td className="py-1 text-gray-800">{String(value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Auth Modal Component
const AuthModal = ({ setCurrentView }) => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    first_name: '',
    last_name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = isLogin
        ? await login(formData.email, formData.password)
        : await register(formData);

      if (result.success) {
        setCurrentView('map');
      } else {
        setError(result.error || 'An error occurred');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded border p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl">👤</span>
          </div>
          <h2 className="text-2xl font-bold">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="text-gray-600 mt-2">
            {isLogin ? 'Sign in to your account' : 'Sign up to get started'}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div onSubmit={handleSubmit}>
          <div className="space-y-6">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required={!isLogin}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={() => setCurrentView('map')}
            className="text-gray-600 hover:text-gray-800"
          >
            Continue as Guest
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
